/**
 * Space type constants matching backend SpaceType enum
 */
export const SPACE_TYPES = {
  NEWSFEED: 'NEWSFEED',
  APPS: 'APPS',
  PAGE: 'PAGE',
  LAUNCHER: 'LAUNCHER',  // DCC launcher spaces
  WIKI: 'WIKI'           // Embedded wiki spaces
};

/**
 * SpaceData class for handling space objects
 * Mirrors the backend SpaceResponse model
 */
export class SpaceData {
  static TYPES = SPACE_TYPES;

  constructor(data) {
    this.space_id = data.space_id;
    this.name = data.name || '';
    this.space_type = data.space_type || SPACE_TYPES.APPS;
    this.icon = data.icon || 'layout-grid';
    this.description = data.description || null;
    this.display_order = data.display_order ?? 0;
    this.is_default = data.is_default ?? false;
    this.is_system = data.is_system ?? false;
    this.is_hidden = data.is_hidden ?? false;
    this.config = data.config || null;
    this.environment_id = data.environment_id || null;  // Global environment link for launcher spaces
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    this.created_by = data.created_by || null;
  }

  /**
   * Check if this is a NewsFeed space
   */
  get isNewsFeed() {
    return this.space_type === SPACE_TYPES.NEWSFEED;
  }

  /**
   * Check if this is an Apps space
   */
  get isApps() {
    return this.space_type === SPACE_TYPES.APPS;
  }

  /**
   * Check if this is a Page space
   */
  get isPage() {
    return this.space_type === SPACE_TYPES.PAGE;
  }

  /**
   * Check if this is a Launcher space
   */
  get isLauncher() {
    return this.space_type === SPACE_TYPES.LAUNCHER;
  }

  /**
   * Check if this is a Wiki space
   */
  get isWiki() {
    return this.space_type === SPACE_TYPES.WIKI;
  }

  /**
   * Check if this space can be deleted (non-system, non-default)
   */
  get canDelete() {
    return !this.is_system && !this.is_default;
  }

  /**
   * Check if this space has limited editability (system spaces)
   */
  get hasLimitedEdit() {
    return this.is_system;
  }

  /**
   * Create SpaceData from API response
   */
  static fromApiResponse(data) {
    return new SpaceData(data);
  }

  /**
   * Create array of SpaceData from API response list
   */
  static fromApiResponseList(dataList = []) {
    return dataList.map(SpaceData.fromApiResponse);
  }

  /**
   * Convert to format suitable for API create/update requests
   */
  toApiFormat() {
    return {
      name: this.name,
      space_type: this.space_type,
      icon: this.icon,
      description: this.description,
      display_order: this.display_order,
      is_hidden: this.is_hidden,
      config: this.config,
      environment_id: this.environment_id
    };
  }

  /**
   * Convert to format for update (only changed fields)
   */
  toUpdateFormat(changes) {
    const format = {};
    if (changes.name !== undefined) format.name = changes.name;
    if (changes.icon !== undefined) format.icon = changes.icon;
    if (changes.description !== undefined) format.description = changes.description;
    if (changes.display_order !== undefined) format.display_order = changes.display_order;
    if (changes.is_hidden !== undefined) format.is_hidden = changes.is_hidden;
    if (changes.config !== undefined) format.config = changes.config;
    if (changes.environment_id !== undefined) format.environment_id = changes.environment_id;
    return format;
  }
}

/**
 * SpacePluginData for plugins within a space
 */
export class SpacePluginData {
  constructor(data) {
    this.plugin_id = data.plugin_id;
    this.name = data.name || '';
    this.category = data.category || '';
    this.display_order = data.display_order ?? 0;
  }

  static fromApiResponse(data) {
    return new SpacePluginData(data);
  }

  static fromApiResponseList(dataList = []) {
    return dataList.map(SpacePluginData.fromApiResponse);
  }
}



